import {useMutation} from '@apollo/client';
import {useCallback} from 'react';
import {get} from 'lodash-es';
import {WizardField} from '../../components/Wizard/QuestionWrapper';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {investmentPhilosophyFields} from './investmentPhilosophyFields';
import {vulnerabilityFields} from './vulnerabilityFields';
import {wizardFieldsType} from '../../components/Wizard/Wizard';
import {ContactAttribution} from '../PrecursorSummaryPage/headerFields';
import {
  GET_ACCOUNT_VULNERABILITIES,
  UPDATE_CONTACT,
} from '../../components/AccountOverview/accountOverviewQueries';
import {useAccountContext} from '../../contexts/AccountContext';
import {getContactIdAndReviewFor, UPDATE_REVIEW} from '../../hooks/useUpdatePrecursor';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {UpdateReview, UpdateReviewVariables} from '../../schema-types/UpdateReview';
import {Vulnerabilities} from '../../schema-types/Vulnerabilities';
import {useReviewContext} from '../../contexts/ReviewContext';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';
import {opportunityFields} from '../PrecursorWizard/fields/opportunityValues';
import {REVIEW_VALIDATION} from '../HandoverSummaryPage/useReviewValidation';
import {ReviewDetailsVariables} from '../../schema-types/ReviewDetails';
import {GetAccountVulnerabilitiesVariables} from '../../schema-types/GetAccountVulnerabilities';
import validateUUID from '../../utils/validateUUID';
import {toUUID} from '../../graphql/uuid';

export const getPostcursorFields = (
  reviewDetails: ReviewDetailsFragment,
  account: AccountDetailsFragment,
  canUseRisk: boolean
): WizardField<postcursorFieldsType>[] => {
  const fields: WizardField<postcursorFieldsType>[] = [];
  fields.push(...opportunityFields(reviewDetails, account));
  // Trust & Corps / Household account fields
  fields.push(...investmentPhilosophyFields(account, canUseRisk));
  if (!account.isTrust) {
    // Household account specific fields
    fields.push(...vulnerabilityFields(account));
  }
  return fields;
};
export type investmentFieldsType = {
  giaHasLiquidityNeeds: {[contactOrAccountId: string]: boolean | null};
  pensionHasLiquidityNeeds: {[contactOrAccountId: string]: boolean | null};
  // account:{
  //   understandsPrivateAssets: {[contactId: string]: boolean};
  //   giaHasLiquidityNeeds: {[contactId: string]: boolean};
  //   pensionHasLiquidityNeeds: {[contactId: string]: boolean};
  // }
};

export type vulnerabilityFieldsType = {
  isVulnerable: {[contactId: string]: boolean};
  longTermVulnerabilityTypes: {[contactId: string]: string[]};
  isEventVulnerable: {[contactId: string]: boolean};
  eventVulnerabilityTypes: {[contactId: string]: string[]};

  specialCareReasons: {[contactId: string]: string | null};
};

function getVulnerabilities(values: postcursorFieldsType, cId: string) {
  const anyLongTerm = get(values, `isVulnerable.${cId}`, false) as boolean;
  const anyEventBased = get(values, `isEventVulnerable.${cId}`, false) as boolean;
  const longTerm = get(values, `longTermVulnerabilityTypes.${cId}`, []) as string[];
  const eventBased = get(values, `eventVulnerabilityTypes.${cId}`, []) as string[];
  const vulnerabilities: Omit<
    Vulnerabilities,
    '__typename' | 'isVulnerable' | 'specialCareReasons'
  > = {
    // long term
    ageVulnerability: anyLongTerm && longTerm.includes('ageVulnerability'),
    genderReassignment: anyLongTerm && longTerm.includes('genderReassignment'),
    healthVulnerability: anyLongTerm && longTerm.includes('healthVulnerability'),
    hearingVulnerability: anyLongTerm && longTerm.includes('hearingVulnerability'),
    mentalCapacityVulnerability:
      anyLongTerm && longTerm.includes('mentalCapacityVulnerability'),
    mentalHealthVulnerability:
      anyLongTerm && longTerm.includes('mentalHealthVulnerability'),
    languageVulnerability: anyLongTerm && longTerm.includes('languageVulnerability'),
    learningVulnerability: anyLongTerm && longTerm.includes('learningVulnerability'),
    visionVulnerability: anyLongTerm && longTerm.includes('visionVulnerability'),
    otherVulnerability: anyLongTerm && longTerm.includes('otherVulnerability'),
    // event
    bereavementVulnerability:
      anyEventBased && eventBased.includes('bereavementVulnerability'),
    divorceVulnerability: anyEventBased && eventBased.includes('divorceVulnerability'),
    financialDifficultiesVulnerability:
      anyEventBased && eventBased.includes('financialDifficultiesVulnerability'),
    inexperienceVulnerability:
      anyEventBased && eventBased.includes('inexperienceVulnerability'),
    otherEventVulnerability:
      anyEventBased && eventBased.includes('otherEventVulnerability'),
  };
  return vulnerabilities;
}

export type postcursorFieldsType = wizardFieldsType &
  investmentFieldsType &
  vulnerabilityFieldsType & {
    reviewContact: ContactAttribution | string;
    opportunityValue: string;
  };

export default function useUpdatePostcursor({
  onError,
  onSuccess,
}: {
  onSuccess?: (reviewDetails: ReviewDetailsFragment) => void;
  onError?: (error?: string) => void;
}): {
  handleUpdatePostcursor: (
    values: postcursorFieldsType,
    canUseRisk: boolean
  ) => Promise<any>;
  loading: boolean;
} {
  const {account, refetchAccount} = useAccountContext();
  const {reviewId} = useReviewContext();
  const reviewDetailsVariables: ReviewDetailsVariables = {
    valueId: reviewId,
  };
  const accountDetailsVariables: GetAccountVulnerabilitiesVariables = {
    accountId: account?.id,
  };
  const [updateReview, {loading}] = useMutation<UpdateReview, UpdateReviewVariables>(
    UPDATE_REVIEW,
    {
      refetchQueries: [
        {
          query: REVIEW_VALIDATION,
          variables: reviewDetailsVariables,
        },
        {
          query: GET_ACCOUNT_VULNERABILITIES,
          variables: accountDetailsVariables,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (data.updateReview?.ok) {
          onSuccess?.(data.updateReview.review!);
        }
        if (!data.updateReview?.ok) {
          onError?.(data.updateReview?.errors?.join(','));
        }
      },
      onError: (error) => {
        if (error.networkError) {
          onError?.('Network error');
        } else {
          onError?.(error.message);
        }
      },
    }
  );
  const [updateContactMutation] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT,
    {
      onCompleted: (data) => {
        if (!data?.updateContact?.ok) {
          onError?.();
        }
      },
      onError: (error) => onError?.(error.message),
    }
  );

  const handleUpdatePostcursor = useCallback(
    async (values: postcursorFieldsType, canUseRisk: boolean) => {
      const trustOnlyVariables: Pick<
        UpdateReviewVariables,
        'accountSubmissionNeeded' | 'giaHasLiquidityNeeds' | 'pensionHasLiquidityNeeds'
      > = {
        giaHasLiquidityNeeds: values.giaHasLiquidityNeeds[account?.id] || false,
        pensionHasLiquidityNeeds: values.pensionHasLiquidityNeeds[account?.id] || false,
        accountSubmissionNeeded: true,
      };
      if (!account?.isTrust) {
        for await (const c of account?.contacts || []) {
          const cId = c.id;
          const updateContactVariables: UpdateContactVariables = {
            contactId: validateUUID(cId)!,
            ...getVulnerabilities(values, cId),
            specialCareReasons: values?.isEventVulnerable[cId]
              ? values?.specialCareReasons[cId]
              : '',
          };
          if (!canUseRisk) {
            updateContactVariables.giaHasLiquidityNeeds =
              values.giaHasLiquidityNeeds[c.id] ?? null;
            updateContactVariables.pensionHasLiquidityNeeds =
              values.pensionHasLiquidityNeeds[c.id] ?? null;
          }
          await updateContactMutation({
            variables: updateContactVariables,
          });
        }
      }
      // Cannot use a hook inside a callback
      // React Hooks must be called in a React function component or a custom React Hook function
      const {reviewFor, contactId} = getContactIdAndReviewFor(values, account);

      const reviewVariables: UpdateReviewVariables = {
        reviewId: validateUUID(reviewId)!,
        postcursorNotFinished: false,
        opportunityValue: values.opportunityValue,
        ...(account?.isTrust ? trustOnlyVariables : {}),
        reviewFor,
        contactId: validateUUID(contactId),
      };
      await updateReview({
        variables: reviewVariables,
      });
      await refetchAccount();
    },
    [updateReview]
  );

  return {handleUpdatePostcursor, loading};
}
